import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Compass, List, X, User, ChartBar, SignOut, Receipt } from "@phosphor-icons/react";

const links = [
  { to: "/explore", label: "Explore" },
  { to: "/chat", label: "AI Concierge" },
  { to: "/boards", label: "Trip Boards" },
  { to: "/shop", label: "Shop" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#FAFAF7]/75 border-b border-[#D2D5CD]">
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          <Compass size={26} weight="duotone" className="text-[#2E523A]" />
          <span className="font-display text-xl font-bold tracking-tight text-[#1A1C19]">
            TravelNext
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                location.pathname === l.to
                  ? "bg-[#2E523A] text-white"
                  : "text-[#1A1C19] hover:bg-[#EAEBE6]"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Link to="/cart" className="relative flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-sm font-semibold text-[#1A1C19] hover:bg-[#EAEBE6] transition-colors duration-200" data-testid="nav-cart">
            Checkout
            {count > 0 && (
              <span className="bg-[#E8B291] text-[#1A1C19] text-[10px] font-bold h-5 min-w-5 px-1 rounded-full flex items-center justify-center" data-testid="cart-count-badge">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="hidden md:flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-[#D2D5CD] hover:border-[#2E523A]/40 transition-colors duration-200" data-testid="nav-user-menu">
                  <span className="h-8 w-8 rounded-full bg-[#2E523A] text-white flex items-center justify-center text-sm font-bold">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </span>
                  <span className="text-sm font-medium max-w-[90px] truncate">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => navigate("/boards")} data-testid="menu-boards">
                  <User size={16} className="mr-2" /> My Trip Boards
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders">
                  <Receipt size={16} className="mr-2" /> My Orders
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <ChartBar size={16} className="mr-2" /> Admin Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <SignOut size={16} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={() => navigate("/login")}
              className="hidden md:inline-flex rounded-full bg-[#2E523A] hover:bg-[#26442f] text-white px-5"
              data-testid="nav-login-btn"
            >
              Sign in
            </Button>
          )}

          <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="md:hidden bg-[#FAFAF7] border-b border-[#D2D5CD] px-5 py-4 flex flex-col gap-1" data-testid="mobile-menu">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="px-4 py-3 rounded-xl text-base font-medium hover:bg-[#EAEBE6]"
              data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </Link>
          ))}
          <div className="h-px bg-[#D2D5CD] my-2" />
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" onClick={() => setOpen(false)} className="px-4 py-3 rounded-xl font-medium hover:bg-[#EAEBE6]">
                  Admin Dashboard
                </Link>
              )}
              <Link to="/orders" onClick={() => setOpen(false)} className="px-4 py-3 rounded-xl font-medium hover:bg-[#EAEBE6]" data-testid="mobile-orders">
                My Orders
              </Link>
              <button onClick={() => { logout(); setOpen(false); }} className="px-4 py-3 rounded-xl font-medium text-left hover:bg-[#EAEBE6]" data-testid="mobile-logout">
                Log out
              </button>
            </>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="px-4 py-3 rounded-xl font-medium bg-[#2E523A] text-white text-center">
              Sign in
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
